// src/lib/socket-client.ts
import type {
  ChatWsMessageIn,
  ChatWsAudioOrVideoIn,
  ServerEvent,
} from "../types/chat";

type Handlers = {
  onOpen?: () => void;
  onClose?: () => void;
  onEvent?: (e: ServerEvent) => void;
  onError?: (e: any) => void;
};

export class SocketClient {
  private url: string;
  private ws?: WebSocket;
  private handlers: Handlers;
  private reconnect = true;
  private retryDelay = 1200;

  constructor(url: string, handlers: Handlers = {}) {
    this.url = url;
    this.handlers = handlers;
  }

  log(...args: any[]) {
    // if (environment !== "prod") console.log(...args);
    console.log(...args);
  }

  connect() {
    if (
      this.ws &&
      (this.ws.readyState === WebSocket.OPEN ||
        this.ws.readyState === WebSocket.CONNECTING)
    ) {
      this.log("[socket] connect() skipped; state=", this.ws.readyState);
      return;
    }
    this.log("[socket] connecting to:", this.url);
    this.ws = new WebSocket(this.url);

    this.ws.onopen = () => {
      this.log("[socket] connection open");
      this.handlers.onOpen?.();
    };

    this.ws.onclose = (ev: CloseEvent) => {
      this.log("[socket] connection closed", {
        code: ev.code,
        reason: ev.reason,
        wasClean: ev.wasClean,
      });
      this.handlers.onClose?.();
      if (this.reconnect) {
        this.log(`[socket] reconnecting in ${this.retryDelay}ms...`);
        setTimeout(() => {
          this.log("[socket] reconnecting now");
          this.connect();
        }, this.retryDelay);
      }
    };

    this.ws.onerror = (err) => {
      this.log("[socket] error", err);
      this.handlers.onError?.(err);
    };

    this.ws.onmessage = (msg) => {
      try {
        // Peek raw message size
        if (typeof msg.data === "string") {
          this.log("[socket] <- message (text)", {
            bytes: msg.data.length,
            data: JSON.parse(msg.data),
          });
        } else {
          this.log("[socket] <- message (binary)");
        }
      } catch {}
      try {
        const data: ServerEvent = JSON.parse(msg.data);
        if ("type" in data) {
          this.log("[socket] <- event", { type: data.type });
        } else if ("error_type" in data) {
          this.log("[socket] <- error event", {
            error_type: data.error_type,
          });
        }
        this.handlers.onEvent?.(data);
      } catch (e) {
        this.log("[socket] failed to parse message", e);
        this.handlers.onError?.(e);
      }
    };
  }

  disconnect() {
    this.log("[socket] manual disconnect requested");
    this.reconnect = false;
    this.ws?.close();
  }

  sendMessage(payload: ChatWsMessageIn) {
    this.log("[socket] -> sendMessage", payload);
    this.ws?.send(JSON.stringify(payload));
  }

  requestAudioOrVideo(payload: ChatWsAudioOrVideoIn) {
    this.log("[socket] -> requestAudioOrVideo", {
      action: payload.action,
      chat_message_id: payload.chat_message_id,
    });
    this.ws?.send(JSON.stringify(payload));
  }

  get ready() {
    this.log("[socket] ready? state=", this.ws?.readyState);
    return this.ws?.readyState === WebSocket.OPEN;
  }
}
